import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Project, FundRequest } from '../../types';
import apiService from '../../services/api';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Badge,
  Grid,
  FlexContainer
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaProjectDiagram, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUsers, 
  FaMoneyBillWave,
  FaChartLine,
  FaEye,
  FaEdit,
  FaArrowLeft
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    font-size: 1.8rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--light-gray);
  color: var(--secondary-color);
  
  &:hover {
    background: var(--medium-gray);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const ProjectHeader = styled.div`
  margin-bottom: 2rem;
  
  .project-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  .project-id {
    font-size: 1.1rem;
    color: var(--dark-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .project-description {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--dark-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .project-status {
    display: inline-block;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
      padding: 0.4rem 0.8rem;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InfoCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .info-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    
    .info-icon {
      font-size: 1.5rem;
      color: var(--primary-color);
      
      @media (max-width: 768px) {
        font-size: 1.3rem;
      }
    }
    
    .info-title {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1.1rem;
      
      @media (max-width: 768px) {
        font-size: 1rem;
      }
    }
  }
  
  .info-content {
    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--light-gray);
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: var(--dark-gray);
        font-weight: 500;
        font-size: 0.9rem;
        
        @media (max-width: 768px) {
          font-size: 0.85rem;
        }
      }
      
      .value {
        font-weight: 600;
        color: var(--secondary-color);
        font-size: 0.95rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
    }
  }
`;

const BudgetCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .budget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .budget-total {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-color);
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
  
  .budget-breakdown {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 0.75rem;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }
  
  .budget-item {
    padding: 1rem;
    background: var(--light-gray);
    border-radius: 8px;
    
    .category {
      font-size: 0.9rem;
      color: var(--dark-gray);
      margin-bottom: 0.5rem;
      font-weight: 500;
      
      @media (max-width: 768px) {
        font-size: 0.85rem;
      }
    }
    
    .amount {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1.1rem;
      
      @media (max-width: 768px) {
        font-size: 1rem;
      }
    }
  }
`;

const ProgressSection = styled.div`
  margin-bottom: 2rem;
  
  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .progress-bar {
    width: 100%;
    height: 12px;
    background-color: var(--light-gray);
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      height: 10px;
    }
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--primary-color), #e07a10);
    transition: width 0.3s ease;
    border-radius: 6px;
  }
  
  .progress-label {
    font-size: 0.9rem;
    color: var(--dark-gray);
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }
`;

const TeamSection = styled.div`
  margin-bottom: 2rem;
`;

const TeamMember = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  .member-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--primary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1rem;
    
    @media (max-width: 768px) {
      width: 35px;
      height: 35px;
      font-size: 0.9rem;
    }
  }
  
  .member-info {
    flex: 1;
    
    .member-name {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1rem;
      margin-bottom: 0.25rem;
      
      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
    
    .member-role {
      font-size: 0.85rem;
      color: var(--dark-gray);
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
  }
  
  .member-status {
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }
`;

const RequestsSection = styled.div`
  margin-bottom: 2rem;
`;

const RequestItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid var(--primary-color);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .request-info {
    flex: 1;
    
    .request-id {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1rem;
      margin-bottom: 0.25rem;
      
      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
    
    .request-details {
      font-size: 0.85rem;
      color: var(--dark-gray);
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
  }
  
  .request-amount {
    font-weight: 700;
    color: var(--primary-color);
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .request-status {
    margin-left: 1rem;
    
    @media (max-width: 768px) {
      margin-left: 0;
    }
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const SupervisorProjectDetail: React.FC = () => {
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [projectRequests, setProjectRequests] = useState<FundRequest[]>([]);

  useEffect(() => {
    const fetchAssignedProject = async () => {
      try {
        // Get projects assigned to the current supervisor
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        // Backend now properly filters by role, so we can use the first project
        const supervisorProject = assignedProjects.length > 0 ? assignedProjects[0] : null;
        
        setProject(supervisorProject);

        if (supervisorProject) {
          // Get fund requests for the assigned project
          const fundRequestsResponse = await apiService.getFundRequests({ 
            projectId: supervisorProject._id || supervisorProject.id 
          });
          const requests = fundRequestsResponse.data || [];
          setProjectRequests(requests);
        }
      } catch (error) {
        console.error("Error fetching assigned project:", error);
      }
    };

    if (user) {
      fetchAssignedProject();
    }
  }, [user]);

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Under Review': return 'warning';
      case 'Paid': return 'info';
      case 'Draft': return 'primary';
      default: return 'warning';
    }
  };

  if (!project) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>Project Details</h1>
          <BackButton as={Link} to="/supervisor">
            <IconWrapper icon={FaArrowLeft} />
            Back to Dashboard
          </BackButton>
        </PageHeader>
        
        <NoDataMessage>
          <div className="empty-icon">🏗️</div>
          <div className="empty-title">No Project Assigned</div>
          <div className="empty-description">
            You are not currently assigned to supervise any project. 
            Please contact your project manager or administrator.
          </div>
        </NoDataMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Project Details</h1>
        <BackButton as={Link} to="/supervisor">
          <IconWrapper icon={FaArrowLeft} />
          Back to Dashboard
        </BackButton>
      </PageHeader>

      <ProjectHeader>
        <div className="project-title">{project.name}</div>
        <div className="project-id">Project ID: {project.id}</div>
        <div className="project-description">{project.description}</div>
        <Badge variant={getStatusVariant(project.status)} className="project-status">
          {project.status}
        </Badge>
      </ProjectHeader>

      <InfoGrid>
        <InfoCard>
          <div className="info-header">
            <IconWrapper icon={FaCalendarAlt} className="info-icon" />
            <div className="info-title">Timeline</div>
          </div>
          <div className="info-content">
            <div className="info-item">
              <span className="label">Start Date</span>
              <span className="value">{formatDate(project.startDate)}</span>
            </div>
            <div className="info-item">
              <span className="label">End Date</span>
              <span className="value">{formatDate(project.endDate || new Date())}</span>
            </div>
                          <div className="info-item">
                <span className="label">Duration</span>
                <span className="value">
                  {project.endDate ? 
                    `${Math.ceil((new Date(project.endDate).getTime() - new Date(project.startDate).getTime()) / (1000 * 60 * 60 * 24))} days` : 
                    'Ongoing'
                  }
                </span>
              </div>
          </div>
        </InfoCard>

        <InfoCard>
          <div className="info-header">
            <IconWrapper icon={FaMapMarkerAlt} className="info-icon" />
            <div className="info-title">Location</div>
          </div>
          <div className="info-content">
            <div className="info-item">
              <span className="label">Site</span>
              <span className="value">{project.location}</span>
            </div>
                        <div className="info-item">
              <span className="label">Type</span>
              <span className="value">{project.type}</span>
            </div>
          </div>
        </InfoCard>

        <InfoCard>
          <div className="info-header">
            <IconWrapper icon={FaUsers} className="info-icon" />
            <div className="info-title">Team</div>
          </div>
          <div className="info-content">
            <div className="info-item">
              
            </div>
            <div className="info-item">
              <span className="label">Staff Count</span>
              <span className="value">{project.assignedStaff?.length || 0}</span>
            </div>
            <div className="info-item">
              <span className="label">Supervisor</span>
              <span className="value">{user?.name}</span>
            </div>
          </div>
        </InfoCard>
      </InfoGrid>

      <BudgetCard>
        <div className="budget-header">
          <div>
            <h3>Budget Overview</h3>
            <div className="budget-total">{formatCurrency(project.budget.total)}</div>
          </div>
        </div>
        
        <div className="budget-breakdown">
          <div className="budget-item">
            <div className="category">Materials</div>
            <div className="amount">{formatCurrency(project.budget.materials)}</div>
          </div>
          <div className="budget-item">
            <div className="category">Labor</div>
            <div className="amount">{formatCurrency(project.budget.labor)}</div>
          </div>
          <div className="budget-item">
            <div className="category">Equipment</div>
            <div className="amount">{formatCurrency(project.budget.equipment)}</div>
          </div>
          <div className="budget-item">
            <div className="category">Sub-contracts</div>
            <div className="amount">{formatCurrency(project.budget.subContracts)}</div>
          </div>
          <div className="budget-item">
            <div className="category">Others</div>
            <div className="amount">{formatCurrency(project.budget.others)}</div>
          </div>
        </div>
      </BudgetCard>

      <ProgressSection>
        <div className="progress-header">
          <h3>Project Progress</h3>
          <div className="progress-label">{project.progress || 0}% Complete</div>
        </div>
        
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${project.progress || 0}%` }}
          />
        </div>
      </ProgressSection>

      <TeamSection>
        <CardHeader>
          <h3>Assigned Team Members</h3>
        </CardHeader>
        
        {project.assignedStaff && project.assignedStaff.length > 0 ? (
          project.assignedStaff.map((member, index) => (
            <TeamMember key={index}>
              <div className="member-avatar">
                {member.name.charAt(0).toUpperCase()}
              </div>
              <div className="member-info">
                <div className="member-name">{member.name}</div>
                <div className="member-role">{member.role}</div>
              </div>
              <Badge variant="success" className="member-status">
                Active
              </Badge>
            </TeamMember>
          ))
        ) : (
          <NoDataMessage>
            <div className="empty-icon">👥</div>
            <div className="empty-title">No Team Members</div>
            <div className="empty-description">
              No team members have been assigned to this project yet.
            </div>
          </NoDataMessage>
        )}
      </TeamSection>

      <RequestsSection>
        <CardHeader>
          <h3>Fund Requests ({projectRequests.length})</h3>
          <Button as={Link} to="/supervisor/funds">
            <IconWrapper icon={FaEye} />
            View All Requests
          </Button>
        </CardHeader>
        
        {projectRequests.length > 0 ? (
          projectRequests.slice(0, 5).map((request) => (
            <RequestItem key={request.id}>
              <div className="request-info">
                <div className="request-id">{request.id}</div>
                <div className="request-details">
                  {request.requester} • {request.timeline} • {formatDate(request.createdAt)}
                </div>
              </div>
              <div className="request-amount">
                {formatCurrency(request.amount, request.currency)}
              </div>
              <div className="request-status">
                <Badge variant={getStatusVariant(request.status)}>
                  {request.status}
                </Badge>
              </div>
            </RequestItem>
          ))
        ) : (
          <NoDataMessage>
            <div className="empty-icon">💰</div>
            <div className="empty-title">No Fund Requests</div>
            <div className="empty-description">
              No fund requests have been created for this project yet.
            </div>
          </NoDataMessage>
        )}
      </RequestsSection>
    </PageContainer>
  );
};

export default SupervisorProjectDetail; 